import { Directive, ElementRef, Renderer2, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { ResizeModel, ResizeTypeModel } from '../model/eng/resize.model';


@Directive({
  selector: '[appResizableDrawer]',
  standalone: true
})
export class ResizableDrawerDirective implements OnInit {
  private _resizeDirection: 'left' | 'right' = 'right';
  private _minWidth: number = 0;
  private _maxWidth: number = null;
  @Input()
  set resizeDirection(value: 'left' | 'right') {
    this._resizeDirection = value;
  }

  get resizeDirection() {
    return this._resizeDirection;
  }

  // minWidth
  @Input()
  set minWidth(value: number) {
    this._minWidth = value;
  }

  get minWidth() {
    return this._minWidth;
  }

  // maxWidth
  @Input()
  set maxWidth(value: number) {
    this._maxWidth = value;
  }

  get maxWidth() {
    return this._maxWidth;
  }

  private resizeHandle: HTMLElement;
  private isResizing = false;
  private startX = 0;
  private startWidth = 0;
  @Output() resizeEvent = new EventEmitter<ResizeModel>(null);

  constructor(private el: ElementRef, private renderer: Renderer2) { }

  ngOnInit() {
    // Apply necessary styles
    this.renderer.setStyle(this.el.nativeElement, 'position', 'relative');
    // this.renderer.setStyle(this.el.nativeElement, 'box-sizing', 'border-box');
    this.renderer.setStyle(this.el.nativeElement, 'transition', 'width 0.2s ease');
    this.renderer.setStyle(this.el.nativeElement, 'min-width', '');
    // Create and append the resize handle
    this.createHandle();
  }

  private createHandle() {
    this.resizeHandle = this.renderer.createElement('div');
    this.renderer.setStyle(this.resizeHandle, 'position', 'absolute');
    this.renderer.setStyle(this.resizeHandle, this.resizeDirection === 'right' ? 'right' : 'left', '0');
    this.renderer.setStyle(this.resizeHandle, 'top', '0');
    this.renderer.setStyle(this.resizeHandle, 'width', '4px');
    this.renderer.setStyle(this.resizeHandle, 'height', '100%');
    this.renderer.setStyle(this.resizeHandle, 'z-index', '2');
    this.renderer.setStyle(this.resizeHandle, 'cursor', 'e-resize');
    this.renderer.setStyle(this.resizeHandle, 'background', 'rgba(0, 0, 0, 0.1)');
    this.renderer.appendChild(this.el.nativeElement, this.resizeHandle);

    // Add event listeners
    this.renderer.listen(this.resizeHandle, 'mousedown', (event: MouseEvent) => this.startResizing(event));
    this.renderer.listen(this.resizeHandle, 'mouseenter', () => this.setHandleHoverColor(true));
    this.renderer.listen(this.resizeHandle, 'mouseleave', () => this.setHandleHoverColor(false));
    this.renderer.listen(this.resizeHandle, 'dblclick', () => this.resetToMinWidth(this.minWidth, this.minWidth));
  }

  private startResizing(event: MouseEvent) {
    event.preventDefault();
    this.isResizing = true;
    this.startX = event.clientX;
    this.startWidth = this.el.nativeElement.offsetWidth;

    // Disable CSS transition during resizing for immediate feedback
    // this.renderer.setStyle(this.el.nativeElement, 'transition', 'none');
    this.renderer.setStyle(this.el.nativeElement, 'min-width', '');
    this.renderer.setStyle(this.el.nativeElement, 'max-width', `${this.maxWidth}px`);
    // Attach event listeners
    document.addEventListener('mousemove', this.throttledResize.bind(this));
    document.addEventListener('mouseup', this.stopResizing.bind(this));
  }

  private throttledResize = (() => {
    let lastExecution = 0;
    return (event: MouseEvent) => {
      const now = Date.now();
      if (now - lastExecution >= 16) { // ~60 FPS
        this.resize(event);
        lastExecution = now;
      }
    };
  })();

  private resize(event: MouseEvent) {
    if (this.isResizing) {
      let newWidth: number;

      if (this.resizeDirection === 'right') {
        newWidth = this.startWidth + (event.clientX - this.startX);
      } else { // 'left'
        newWidth = this.startWidth - (event.clientX - this.startX);
      }

      if (newWidth < this.minWidth) {
        this.renderer.setStyle(this.resizeHandle, 'background', 'red');
      } else if (newWidth >= this.maxWidth) {
        this.renderer.setStyle(this.resizeHandle, 'background', 'red');
        newWidth = this.maxWidth;
      } else {
        this.renderer.setStyle(this.resizeHandle, 'background', '#074075');
      }

      this.renderer.setStyle(this.el.nativeElement, 'width', `${newWidth}px`);
    }
  }

  private setHandleHoverColor(isHovered: boolean) {
    if (!this.isResizing) {
      this.renderer.setStyle(this.resizeHandle, 'background', isHovered ? '#074075' : 'rgba(0, 0, 0, 0.1)');
    }
  }

  private stopResizing() {
    this.isResizing = false;
    // Re-enable CSS transition for smooth effects after resizing
    this.renderer.setStyle(this.el.nativeElement, 'transition', 'width 0.2s ease');
    this.renderer.setStyle(this.resizeHandle, 'background', 'rgba(0, 0, 0, 0.1)');
    // Remove event listeners
    document.removeEventListener('mousemove', this.throttledResize);
    document.removeEventListener('mouseup', this.stopResizing);
    // Call the callback function after resizing stops
    this.onResizeStop();
  }

  private onResizeStop() {
    // Logic to execute after resizing stops
    if (this.el.nativeElement.offsetWidth < this.minWidth) {
      this.resetToMinWidth(this.el.nativeElement.offsetWidth, this.minWidth)
    }
  }

  private resetToMinWidth(width, minWidth) {
    this.renderer.setStyle(this.el.nativeElement, 'width', `${minWidth}px`);
    const resizeEvent = new ResizeModel();
    resizeEvent.type = ResizeTypeModel.CLOSE;
    resizeEvent.width = width;
    resizeEvent.minWidth = minWidth;
    this.resizeEvent.emit(resizeEvent);
  }
}
